import type { FunctionComponent } from 'react';
import { lazy, Suspense } from 'react';

import { createUrlTileDefinition, type UrlTileModule, type UrlTileRenderContext, useUrlTiles } from '../url-tiles';
import { PositionTileLoading } from './position-detail/PositionTileFrame';
import {
    POSITION_MANAGEMENT_CHANGELOG_TILE_SLOT_KEY,
    POSITION_MANAGEMENT_COMPARE_TILE_SLOT_KEY,
    POSITION_MANAGEMENT_POSITION_TILE_SLOT_KEY,
    POSITION_MANAGEMENT_TASK_TILE_SLOT_KEY,
    type PositionChangelogTilePayload,
    type PositionCompareTilePayload,
    type PositionTaskTilePayload,
    type PositionTilePayload,
    positionChangelogTileCodec,
    positionChangelogTileKey,
    positionChangelogTileSchema,
    positionCompareTileCodec,
    positionCompareTileSchema,
    positionTaskTileCodec,
    positionTaskTileSchema,
    positionTileCodec,
    positionTileKey,
    positionTilePayloadAtVersion,
    positionTileSchema,
} from './position-tile-payload';
import { positionManagementLegacyUrlTileRewriter } from './url-tile-legacy-rewriter';

const POSITION_TILE_WIDTH = '50%';
const POSITION_CHANGELOG_TILE_WIDTH = '500px';

const PositionDetailTile = lazy(async () => {
    const { PositionDetailTile } = await import('./position-detail/PositionDetailTile');

    return { default: PositionDetailTile };
});

const PositionCompareTile = lazy(async () => {
    const { PositionCompareTile } = await import('./position-detail/PositionCompareTile');

    return { default: PositionCompareTile };
});

const PositionTaskTile = lazy(async () => {
    const { PositionTaskTile } = await import('./position-detail/PositionTaskTile');

    return { default: PositionTaskTile };
});

const PositionChangelogTile = lazy(async () => {
    const { PositionChangelogTile } = await import('./position-changelog/PositionChangelogTile');

    return { default: PositionChangelogTile };
});

type PositionTileProps = {
    readonly context: UrlTileRenderContext;
    readonly payload: PositionTilePayload;
};

export const positionUrlTileDefinition = createUrlTileDefinition({
    codec: positionTileCodec,
    maxWidth: POSITION_TILE_WIDTH,
    minWidth: POSITION_TILE_WIDTH,
    render: ({ context, payload }: PositionTileProps) => {
        const close = (): void => context.closeTile(POSITION_MANAGEMENT_POSITION_TILE_SLOT_KEY);

        return (
            <Suspense fallback={<PositionTileLoading onClose={close} />}>
                <PositionDetailTile context={context} payload={payload} onClose={close} />
            </Suspense>
        );
    },
    schema: positionTileSchema,
    slotKey: () => POSITION_MANAGEMENT_POSITION_TILE_SLOT_KEY,
    tileKey: (payload) => positionTileKey(payload.positionId),
    toProps: (payload, context): PositionTileProps => ({ context, payload }),
});

type PositionChangelogTileProps = {
    readonly context: UrlTileRenderContext;
    readonly payload: PositionChangelogTilePayload;
};

const PositionChangelogUrlTile: FunctionComponent<PositionChangelogTileProps> = ({ context, payload }) => {
    const { tiles } = useUrlTiles();
    const close = (): void => context.closeTile(POSITION_MANAGEMENT_CHANGELOG_TILE_SLOT_KEY);

    return (
        <Suspense fallback={<PositionTileLoading onClose={close} />}>
            <PositionChangelogTile
                positionId={payload.positionId}
                preferredLanguage={payload.preferredLanguage}
                onClose={close}
                onOpenVersion={(version) =>
                    context.openTile(
                        positionTilePayloadAtVersion({
                            openTiles: tiles,
                            positionId: payload.positionId,
                            preferredLanguage: payload.preferredLanguage,
                            version,
                        }),
                    )
                }
            />
        </Suspense>
    );
};

export const positionChangelogUrlTileDefinition = createUrlTileDefinition({
    codec: positionChangelogTileCodec,
    holdAutoScroll: true,
    maxWidth: POSITION_CHANGELOG_TILE_WIDTH,
    minWidth: POSITION_CHANGELOG_TILE_WIDTH,
    render: (props: PositionChangelogTileProps) => <PositionChangelogUrlTile {...props} />,
    schema: positionChangelogTileSchema,
    slotKey: () => POSITION_MANAGEMENT_CHANGELOG_TILE_SLOT_KEY,
    tileKey: (payload) => positionChangelogTileKey(payload.positionId),
    toProps: (payload, context): PositionChangelogTileProps => ({ context, payload }),
});

type PositionCompareTileProps = {
    readonly context: UrlTileRenderContext;
    readonly payload: PositionCompareTilePayload;
};

const POSITION_COMPARE_TILE_WIDTH = '50%';

export const positionCompareUrlTileDefinition = createUrlTileDefinition({
    codec: positionCompareTileCodec,
    maxWidth: POSITION_COMPARE_TILE_WIDTH,
    minWidth: POSITION_COMPARE_TILE_WIDTH,
    render: ({ context, payload }: PositionCompareTileProps) => (
        <Suspense
            fallback={
                <PositionTileLoading onClose={() => context.closeTile(POSITION_MANAGEMENT_COMPARE_TILE_SLOT_KEY)} />
            }
        >
            <PositionCompareTile context={context} payload={payload} />
        </Suspense>
    ),
    schema: positionCompareTileSchema,
    slotKey: () => POSITION_MANAGEMENT_COMPARE_TILE_SLOT_KEY,
    tileKey: (payload) => `${POSITION_MANAGEMENT_COMPARE_TILE_SLOT_KEY}.${payload.positionId}`,
    toProps: (payload, context): PositionCompareTileProps => ({ context, payload }),
});

type PositionTaskTileProps = {
    readonly context: UrlTileRenderContext;
    readonly payload: PositionTaskTilePayload;
};

const POSITION_TASK_TILE_WIDTH = '500px';

export const positionTaskUrlTileDefinition = createUrlTileDefinition({
    codec: positionTaskTileCodec,
    maxWidth: POSITION_TASK_TILE_WIDTH,
    minWidth: POSITION_TASK_TILE_WIDTH,
    render: ({ context, payload }: PositionTaskTileProps) => (
        <Suspense
            fallback={<PositionTileLoading onClose={() => context.closeTile(POSITION_MANAGEMENT_TASK_TILE_SLOT_KEY)} />}
        >
            <PositionTaskTile context={context} payload={payload} />
        </Suspense>
    ),
    schema: positionTaskTileSchema,
    slotKey: () => POSITION_MANAGEMENT_TASK_TILE_SLOT_KEY,
    tileKey: () => POSITION_MANAGEMENT_TASK_TILE_SLOT_KEY,
    toProps: (payload, context): PositionTaskTileProps => ({ context, payload }),
});

export const positionManagementUrlTileModule: UrlTileModule = {
    definitions: [
        positionUrlTileDefinition,
        positionChangelogUrlTileDefinition,
        positionCompareUrlTileDefinition,
        positionTaskUrlTileDefinition,
    ],
    legacyRewriters: [positionManagementLegacyUrlTileRewriter],
};
